/**
 * 教师洞察报告 + 原文闸门 — teacher-role spec §5.6 (M5)。
 * 生成(202+轮询)→ 卡片式渲染(E7 风格)→ 导出 PDF;
 * 会话列表每条「查看原文」需填理由(记 append-only 审计)。
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { authHeaders } from "@/api/school";
import { downloadReportPdf } from "../reports/downloadReportPdf";

interface Insight {
  status: string;
  confidence?: string;
  error?: string;
  knowledge_difficulties?: { topic: string; evidence: string; frequency?: string }[];
  teaching_challenges?: { challenge: string; evidence: string }[];
  usage_profile?: { active_modules?: string[]; artifact_output?: string; engagement_note?: string };
  suggested_support?: string[];
}

interface SessionRow {
  id: string; title: string; agent_id: string; message_count: number; last_active_at: string | null;
}

export function TeacherInsightDialog({
  teacher,
  onClose,
}: {
  teacher: { nova_user_id: string; name: string };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const today = new Date();
  const threeWeeksAgo = new Date(today.getTime() - 21 * 86400_000);
  const [dateFrom] = useState(threeWeeksAgo.toISOString().slice(0, 10));
  const [dateTo] = useState(today.toISOString().slice(0, 10));
  const [insight, setInsight] = useState<Insight | null>(null);
  const [busy, setBusy] = useState(false);
  const [sessions, setSessions] = useState<SessionRow[] | null>(null);
  const [transcript, setTranscript] = useState<{ title: string; messages: { role: string; content: string }[] } | null>(null);
  const [reasonFor, setReasonFor] = useState<SessionRow | null>(null);
  const [reason, setReason] = useState("");
  const [gateErr, setGateErr] = useState<string | null>(null);
  const printRef = useRef<HTMLDivElement | null>(null);

  async function generate() {
    setBusy(true);
    setInsight(null);
    const resp = await fetch("/api/school/admin/teacher-insights", {
      method: "POST",
      credentials: "include",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ teacher_id: teacher.nova_user_id, date_from: dateFrom, date_to: dateTo }),
    });
    const body = await resp.json();
    const id = body.insight_id;
    const poll = async () => {
      const r = await fetch(`/api/school/admin/teacher-insights/${id}`, {
        credentials: "include", headers: authHeaders(),
      });
      const d = (await r.json()) as Insight;
      if (d.status === "generating") {
        setTimeout(() => void poll(), 3000);
        return;
      }
      setInsight(d);
      setBusy(false);
    };
    void poll();
  }

  useEffect(() => {
    void (async () => {
      const r = await fetch(`/api/school/admin/teachers/${teacher.nova_user_id}/sessions`, {
        credentials: "include", headers: authHeaders(),
      });
      if (r.ok) setSessions(await r.json());
    })();
  }, [teacher.nova_user_id]);

  async function openTranscript(s: SessionRow, reasonText: string) {
    setGateErr(null);
    const r = await fetch(`/api/school/admin/transcripts/${s.id}/access`, {
      method: "POST",
      credentials: "include",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ reason: reasonText }),
    });
    const body = await r.json();
    if (!r.ok) {
      setGateErr(body?.detail?.message ?? `HTTP ${r.status}`);
      return;
    }
    setReasonFor(null);
    setReason("");
    setTranscript(body);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" onClick={onClose}>
      <div className="w-[44rem] max-h-[88vh] overflow-y-auto rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-slate-800">
            {t("insights.title", { name: teacher.name })}
          </h2>
          <div className="flex gap-2">
            {insight?.status === "ready" && (
              <button
                onClick={async () => {
                  if (printRef.current)
                    await downloadReportPdf({
                      element: printRef.current,
                      filename: `${teacher.name}_教学洞察_${dateTo}`,
                    });
                }}
                className="px-2.5 py-1 rounded border border-slate-300 text-xs text-slate-600 hover:bg-slate-50"
              >
                {t("insights.exportPdf")}
              </button>
            )}
            <button
              onClick={generate}
              disabled={busy}
              className="px-3 py-1 rounded bg-sky-600 text-white text-xs disabled:opacity-50"
            >
              {busy ? t("insights.generating") : t("insights.generate")}
            </button>
          </div>
        </div>
        <p className="text-xs text-slate-400 mb-3">{t("insights.range", { from: dateFrom, to: dateTo })}</p>

        {insight?.status === "failed" && (
          <p className="mb-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
            {t("insights.failed")}:{insight.error}
          </p>
        )}

        {insight?.status === "ready" && (
          <div ref={printRef} className="bg-white">
            {insight.confidence === "low_data" && (
              <p className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {t("insights.lowData")}
              </p>
            )}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="rounded-lg border border-rose-100 bg-rose-50/40 p-3">
                <h3 className="text-xs font-semibold text-rose-700 mb-2">{t("insights.difficulties")}</h3>
                {(insight.knowledge_difficulties ?? []).map((d, i) => (
                  <div key={i} className="mb-1.5 text-xs text-slate-700">
                    <b>{d.topic}</b>
                    {d.frequency && <span className="ml-1 text-slate-400">({d.frequency})</span>}
                    <div className="text-slate-500">{d.evidence}</div>
                  </div>
                ))}
              </div>
              <div className="rounded-lg border border-amber-100 bg-amber-50/40 p-3">
                <h3 className="text-xs font-semibold text-amber-700 mb-2">{t("insights.challenges")}</h3>
                {(insight.teaching_challenges ?? []).map((c, i) => (
                  <div key={i} className="mb-1.5 text-xs text-slate-700">
                    <b>{c.challenge}</b>
                    <div className="text-slate-500">{c.evidence}</div>
                  </div>
                ))}
              </div>
            </div>
            {insight.usage_profile && (
              <div className="mb-3 rounded-lg border border-slate-200 p-3 text-xs text-slate-600">
                <h3 className="font-semibold text-slate-700 mb-1">{t("insights.usage")}</h3>
                {insight.usage_profile.active_modules && (
                  <p>{t("insights.activeModules")}:{insight.usage_profile.active_modules.join("、")}</p>
                )}
                {insight.usage_profile.artifact_output && <p>{insight.usage_profile.artifact_output}</p>}
                {insight.usage_profile.engagement_note && <p>{insight.usage_profile.engagement_note}</p>}
              </div>
            )}
            {insight.suggested_support && insight.suggested_support.length > 0 && (
              <div className="mb-3 rounded-lg border border-emerald-100 bg-emerald-50/40 p-3">
                <h3 className="text-xs font-semibold text-emerald-700 mb-1">{t("insights.support")}</h3>
                <ul className="list-disc pl-4 text-xs text-slate-700 space-y-0.5">
                  {insight.suggested_support.map((s2, i) => <li key={i}>{s2}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* 会话列表(原文闸门) */}
        {sessions && sessions.length > 0 && (
          <div className="mt-2">
            <h3 className="text-xs font-semibold text-slate-500 uppercase mb-1.5">{t("insights.sessions")}</h3>
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg max-h-44 overflow-y-auto">
              {sessions.map((s2) => (
                <li key={s2.id} className="flex items-center px-3 py-1.5 text-xs">
                  <span className="truncate text-slate-700">{s2.title}</span>
                  <span className="ml-2 text-slate-400 shrink-0">{s2.message_count} 条</span>
                  <button
                    onClick={() => { setReasonFor(s2); setReason(""); setGateErr(null); }}
                    className="ml-auto shrink-0 px-2 py-0.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50"
                  >
                    {t("insights.viewTranscript")}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 理由弹窗:二次确认 + 必填理由 + 留痕提示 */}
        {reasonFor && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50" onClick={() => setReasonFor(null)}>
            <div className="w-[24rem] rounded-xl bg-white p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <h4 className="text-sm font-semibold text-slate-800 mb-1">{t("insights.gateTitle")}</h4>
              <p className="mb-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                {t("insights.gateWarning")}
              </p>
              <textarea
                autoFocus
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("insights.gateReasonPlaceholder")}
                rows={2}
                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm mb-2"
              />
              {gateErr && <p className="mb-2 text-xs text-rose-600">{gateErr}</p>}
              <div className="flex justify-end gap-2">
                <button onClick={() => setReasonFor(null)} className="px-3 py-1 rounded border border-slate-200 text-xs text-slate-600">
                  {t("common.cancel")}
                </button>
                <button
                  onClick={() => void openTranscript(reasonFor, reason)}
                  disabled={!reason.trim()}
                  className="px-3 py-1 rounded bg-sky-600 text-white text-xs disabled:opacity-50"
                >
                  {t("insights.gateConfirm")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 原文查看器 */}
        {transcript && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50" onClick={() => setTranscript(null)}>
            <div className="w-[36rem] max-h-[80vh] overflow-y-auto rounded-xl bg-white p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <h4 className="text-sm font-semibold text-slate-800 mb-2">{transcript.title}</h4>
              <div className="space-y-2">
                {transcript.messages.map((m, i) => (
                  <div key={i} className={`rounded-lg px-3 py-2 text-xs whitespace-pre-wrap ${
                    m.role === "user" ? "bg-sky-50 text-slate-800" : "bg-slate-50 text-slate-600"
                  }`}>
                    <span className="font-semibold mr-1">{m.role === "user" ? "师" : "AI"}</span>
                    {m.content}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
